import { Card, Icon, Switch, FormControlLabel } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import Alert from "custom/Alert";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import { useLocation, useHistory } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import axios from "axios";
import Swal from "sweetalert2";
import { PDFDocument } from "pdf-lib";
import { connect } from "react-redux";
import { createSoundMaterial as createSoundMaterialAction } from "redux/actions/pages/sound-material";
import PropTypes from "prop-types";
import { BASE_URL } from "redux/actions/constants";
import { SortableMaterials } from "./SortableMaterials";

function SelectedMaterial({ createSoundMaterial }) {
  const location = useLocation();
  const history = useHistory();
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [finalMaterialName, setFinalMaterialName] = useState("");
  const [isEditing, setIsEditing] = useState(true);
  const [finalState, setFinalState] = useState(null);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mergedPdfUrl, setMergedPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedLettersString, setSelectedLettersString] = useState("");
  const [materialTypesString, setMaterialTypesString] = useState("");
  const [pdfPageCount, setPdfPageCount] = useState(null);
  const previewRef = useRef(true);
  const savedRef = useRef(false);

  useEffect(() => {
    // Get the selected materials and material name from the location state
    if (location.state?.selectedMaterials) {
      setSelectedMaterials(location.state.selectedMaterials);
    }
    if (location.state?.materialName) {
      setFinalMaterialName(location.state.materialName);
    }
    if (location.state?.selectedLettersString) {
      setSelectedLettersString(location.state.selectedLettersString);
    }
    if (location.state?.materialTypesString) {
      setMaterialTypesString(location.state.materialTypesString);
    }
  }, [location.state]);

  const handleNameChange = (e) => {
    setFinalMaterialName(e.target.value);
  };

  const handleSaveName = () => {
    setIsEditing(false);
    // Update the final state with the new material name
    setFinalState({
      materialName: finalMaterialName,
      selectedMaterials,
      selectedLettersString,
      materialTypesString,
    });
  };

  const handleEditName = () => {
    setIsEditing(true);
  };

  const handleSaveMaterial = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setMergedPdfUrl(null);

      // Create a new PDF document
      const mergedPdf = await PDFDocument.create();

      // Sort materials by selectionOrder to ensure correct order in the final PDF
      const sortedMaterials = [...selectedMaterials].sort(
        (a, b) => a.selectionOrder - b.selectionOrder
      );

      // Fetch all PDFs in parallel
      const pdfPromises = sortedMaterials.map(async (material) => {
        try {
          // Fetch the PDF from the URL
          const response = await fetch(material.fileUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.statusText}`);
          }

          const pdfBytes = await response.arrayBuffer();
          return { pdfDoc: await PDFDocument.load(pdfBytes), name: material.name };
        } catch (err) {
          throw new Error(`PDF işlenirken hata oluştu: ${material.name}`);
        }
      });

      // Wait for all PDFs to be fetched
      const pdfResults = await Promise.all(pdfPromises);

      // Process each PDF and add its pages to the merged PDF
      const pagePromises = pdfResults.map(async ({ pdfDoc }) => {
        const pageIndices = pdfDoc.getPageIndices();
        const copiedPages = await mergedPdf.copyPages(pdfDoc, pageIndices);
        return copiedPages;
      });

      // Wait for all pages to be copied
      const allPages = await Promise.all(pagePromises);

      const totalPageCount = allPages.reduce((total, pages) => total + pages.length, 0);
      setPdfPageCount(totalPageCount);

      // Flatten the array of arrays and add each page to the merged PDF
      allPages.flat().forEach((page) => {
        mergedPdf.addPage(page);
      });

      // Save the merged PDF
      const mergedPdfBytes = await mergedPdf.save();

      // Create a download URL
      const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setMergedPdfUrl(url);

      // Update final state
      setFinalState({
        materialName: finalMaterialName,
        selectedMaterials: sortedMaterials, // Use the sorted materials in the final state
        selectedLettersString,
        materialTypesString,
      });
    } catch (err) {
      setError(err.message || "PDF'leri birleştirirken bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadToDigitalOcean = async () => {
    if (!mergedPdfUrl) {
      Swal.fire({
        icon: "error",
        title: "Hata!",
        text: "Önce PDF oluşturmalısınız.",
        confirmButtonText: "Tamam",
      });
      return;
    }

    try {
      setIsUploading(true);

      // Fetch the PDF from the URL
      const response = await fetch(mergedPdfUrl);
      const pdfBlob = await response.blob();

      // Create a unique filename with timestamp and random string
      const timestamp = new Date().getTime();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileName = `${timestamp}_${randomString}.pdf`;

      // Create a file from the blob
      const file = new File([pdfBlob], fileName, { type: "application/pdf" });

      // Get presigned URL from your API
      const { data } = await axios.get(
        `${BASE_URL}/api/upload/get-do-presigned-url?type=${file.type}&folder=${fileName}&extention=pdf`
      );

      const { key, url } = data;

      // Upload to DigitalOcean
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.setRequestHeader("x-amz-acl", "public-read");

      // Send the file
      xhr.send(file);

      // Wait for the upload to complete
      await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(xhr.statusText));
          }
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
      });

      // Construct the accessible URL - use only the key without any additional path
      const fileUrl = `https://edictdigitalspaces.fra1.digitaloceanspaces.com/${key}`;

      // Disable edit mode and name editing after successful upload
      setIsReorderMode(false);
      setIsEditing(false);

      // Create the final object with all required properties
      const finalObject = {
        name: finalMaterialName,
        file: fileUrl,
        letters: selectedLettersString,
        isActive: true,
        soundOrder: 0,
        type: null,
      };

      // Update final state to reflect that editing is disabled
      setFinalState({
        ...finalState,
        isEditingDisabled: true,
        isReorderModeDisabled: true,
        selectedLettersString,
        materialTypesString,
        finalObject,
      });

      // Create sound material according to backend API structure
      const soundMaterial = {
        letter: selectedLettersString,
        file: fileUrl,
        name: finalMaterialName,
        isActive: true,
        soundOrder: 0,
      };

      // Call the createSoundMaterial action
      createSoundMaterial(soundMaterial);

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Başarılı!",
        text: "PDF başarıyla yüklendi ve materyal kaydedildi.",
        confirmButtonText: "Tamam",
      }).then(() => {
        // Yönlendirme işlemi
        history.push("/sound-materials");
      });
    } catch (uploadError) {
      Swal.fire({
        icon: "error",
        title: "Hata!",
        text: "PDF yüklenirken bir hata oluştu.",
        confirmButtonText: "Tamam",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const showInfoMessage = () => {
    if (mergedPdfUrl) {
      const newSwal = Swal.mixin({
        customClass: {
          confirmButton: "button button-info",
          cancelButton: "button button-success",
        },
        buttonsStyling: false,
      });

      if (pdfPageCount % 4 === 0) {
        if (previewRef.current) {
          window.open(mergedPdfUrl, "_blank");
        } else {
          handleUploadToDigitalOcean();
        }
      } else {
        newSwal
          .fire({
            icon: "info",
            title: "Uyarı!",
            text: `Oluşturduğunuz döküman ${pdfPageCount} sayfa içeriyor. Yazdırma sırasında bu dökümana boş sayfa eklenebilir. Devam etmek istiyor musunuz?`,
            confirmButtonText: "Tamam",
            cancelButtonText: "Düzenlemeye Dön",
            showCancelButton: true,
          })
          .then((result) => {
            if (result.isConfirmed && previewRef.current) {
              window.open(mergedPdfUrl, "_blank");
            } else if (result.isDismissed) {
              previewRef.current = false;
              savedRef.current = false;
              setIsReorderMode(true);
            }
            if (savedRef.current) {
              handleUploadToDigitalOcean();
            }
          });
      }
    }
  };

  const handlePreviewPdf = () => {
    previewRef.current = true;
    savedRef.current = false;
    showInfoMessage();
  };

  const handleDragEnd = (event) => {
    const { active: draggedTask, over: targetTask } = event;

    if (isReorderMode) {
      if (draggedTask.id !== targetTask.id) {
        const oldIndex = selectedMaterials.findIndex(
          (item) => item.selectionOrder === draggedTask.id
        );
        const newIndex = selectedMaterials.findIndex(
          (item) => item.selectionOrder === targetTask.id
        );

        const newItems = Array.from(selectedMaterials);
        const [removed] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, removed);

        const updatedItems = newItems.map((item, index) => ({
          ...item,
          selectionOrder: index + 1,
        }));

        setSelectedMaterials(updatedItems);
        setMergedPdfUrl(null);
        if (finalState) {
          setFinalState({
            ...finalState,
            selectedMaterials: updatedItems,
            selectedLettersString,
            materialTypesString,
          });
        }
      }
    }
  };

  return (
    <>
      <Alert />
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox mt={3} mb={4}>
          <Card>
            <SuiBox p={3}>
              {/* Material Name Section */}
              <SuiBox mb={4}>
                <SuiTypography variant="h5" fontWeight="medium" mb={2}>
                  Materyal Adı
                </SuiTypography>
                {isEditing && !finalState?.isEditingDisabled ? (
                  <SuiBox display="flex" alignItems="center" gap={2}>
                    <SuiInput
                      value={finalMaterialName}
                      onChange={handleNameChange}
                      placeholder="Materyal adını giriniz"
                      sx={{ width: "300px" }}
                    />
                    <SuiButton variant="gradient" color="dark" onClick={handleSaveName}>
                      Kaydet
                    </SuiButton>
                  </SuiBox>
                ) : (
                  <SuiBox display="flex" alignItems="center" gap={2}>
                    <SuiTypography variant="h6">{finalMaterialName}</SuiTypography>
                    {!finalState?.isEditingDisabled && (
                      <SuiButton
                        variant="outlined"
                        color="dark"
                        circular
                        iconOnly
                        onClick={handleEditName}
                      >
                        <Icon>edit</Icon>
                      </SuiButton>
                    )}
                  </SuiBox>
                )}
              </SuiBox>

              {/* Selected Materials Display with Reorder Mode Switch */}
              <SuiBox mb={4}>
                <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <SuiTypography variant="h5" fontWeight="medium">
                    Seçtiğim Materyaller
                  </SuiTypography>
                  {!finalState?.isReorderModeDisabled && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isReorderMode}
                          onChange={(e) => setIsReorderMode(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Düzenleme Modu"
                    />
                  )}
                </SuiBox>
                {isReorderMode && (
                  <SuiTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                    Materyalleri sürükleyerek sıralayabilirsiniz
                  </SuiTypography>
                )}
                <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
                  <SortableContext
                    items={selectedMaterials.map((material) => material.selectionOrder)}
                    strategy={rectSortingStrategy}
                  >
                    <SuiBox
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(198px, 1fr))",
                        gap: 2,
                        width: "100%",
                      }}
                    >
                      {[...selectedMaterials]
                        .sort((a, b) => a.selectionOrder - b.selectionOrder)
                        .map((material, index) => (
                          <SortableMaterials
                            key={material.selectionOrder}
                            id={material.selectionOrder}
                            material={material}
                            setSelectedMaterials={setSelectedMaterials}
                            setMergedPdfUrl={setMergedPdfUrl}
                            setFinalState={setFinalState}
                            selectedLettersString={selectedLettersString}
                            materialTypesString={materialTypesString}
                            finalState={finalState}
                            selectedMaterials={selectedMaterials}
                            isReorderMode={isReorderMode}
                            index={index}
                          />
                        ))}
                    </SuiBox>
                  </SortableContext>
                </DndContext>
              </SuiBox>

              {/* Error Message */}
              {error && (
                <SuiBox mb={4}>
                  <SuiTypography variant="caption" color="error">
                    {error}
                  </SuiTypography>
                </SuiBox>
              )}

              {/* Action Buttons */}
              <SuiBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                {!mergedPdfUrl ? (
                  <SuiButton
                    variant="gradient"
                    color="dark"
                    onClick={handleSaveMaterial}
                    disabled={isEditing || !finalMaterialName || isLoading}
                  >
                    {isLoading ? "PDF Oluşturuluyor..." : "PDF Oluştur"}
                  </SuiButton>
                ) : (
                  <>
                    <SuiButton variant="gradient" color="info" onClick={handlePreviewPdf}>
                      Önizleme
                    </SuiButton>
                    <SuiButton
                      variant="gradient"
                      color="success"
                      onClick={() => {
                        savedRef.current = true;
                        previewRef.current = false;
                        showInfoMessage();
                      }}
                      disabled={isUploading}
                    >
                      {isUploading ? "Yükleniyor..." : "Kaydet"}
                    </SuiButton>
                  </>
                )}
              </SuiBox>
            </SuiBox>
          </Card>
        </SuiBox>
      </DashboardLayout>
    </>
  );
}

SelectedMaterial.propTypes = {
  createSoundMaterial: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  createSoundMaterial: createSoundMaterialAction,
};

export default connect(null, mapDispatchToProps)(SelectedMaterial);
